import {
  config,
  GENDER,
  REPRO_KIND,
  PREGNANCY_OUTCOME,
  SIBLING_TYPE,
  TWIN_TYPE
} from './config';
import { normalizePregnancyRecord } from './pregnancyNormalize';
import { SIBLING_TYPE_BY_COUNT, ZYGOSITY_TO_TWIN_TYPE } from './twinLinkage';
import { deriveProxyNodes } from './deriveProxies';

// Sibship type by how many fetuses one pregnancy draws — the layout's twin sub-group reads this.

// A pregnancy's recorded zygosity → the twin-line notation (Bennett). Anything unrecognised or
// absent falls through to `unknown`, which draws the "?" — the honest reading when zygosity has not
// been established.

// Track B (pregnancy-record model): the pedigree DERIVES its pregnancy + gamete-donor
// render nodes from the carrier's pregnancy records (Pregnancy collection, surfaced on each
// member as `pregnancies: [record]`). Derived nodes are NOT persons — they exist only in
// the render's normalized node array, so they never get a UPN, a person sidebar, or a place
// in member-based exports. This is the seam that replaces the B1a/B1b config-schema storage;
// the layout engine + Track A symbols/lines consume the output unchanged.
//
// A pregnancy record where `conceivedWith === 'partner'` (or 'partnerAndDonor') descends
// from the carrier+partner union; a donor-conceived one hangs off the carrier alone and the
// donors are satellites (donorOfPids). A live birth (or a record with `bornMemberId`) renders
// NO pregnancy node — it has become a real child; the record is kept only as history. A
// `hidden` record is kept (still listed in the Reproductive sidebar) but not drawn.
//
// PURE + no d3 → unit-testable. Inert (returns the input array) when the flag is off or no
// member carries a pregnancy record, so pedigrees without reproductive data are unchanged.

const DONOR_GENDER = {
  sperm: GENDER.male,
  ovum: GENDER.female,
  embryo: GENDER.unknown
};

// The sex to OPEN a newly created donor person with (TGCLOUD-1597) — still editable on their record
// afterwards, exactly like any other person. Shares DONOR_GENDER with the satellite shapes so a
// donor's symbol cannot depend on which of the two routes created them.
export const donorRoleGender = (role) => DONOR_GENDER[role] ?? GENDER.unknown;

// Does this FETUS draw a symbol on the pedigree? A born fetus has become a real person and is
// drawn as one; a live birth with no linked member yet draws nothing (the clinician has still to
// add the child); `hideInPedigree` is the per-fetus hide. The record-level `hidden` is checked
// separately, upstream, because it suppresses every fetus of the pregnancy.
export const fetusRendersAsNode = (fetus) =>
  !!fetus &&
  !fetus.hideInPedigree &&
  fetus.outcome !== PREGNANCY_OUTCOME.liveBirth &&
  fetus.bornMemberId == null;

const partnerConceived = (rec) =>
  rec.conceivedWith === 'partner' || rec.conceivedWith === 'partnerAndDonor';

// TGCLOUD-1597 — an IDENTIFIED donor is a REAL member, so NO satellite is minted for it. The
// member itself carries the `donorOfPids` edge and picks up the identical `repro-descent` diagonal
// in the layout: the notation must not change with whether we happen to know the donor's name.
// Returns the member on success, or null to fall back to the anonymous satellite.
//
// Deliberately does NOT set `reproKind` on the member, and that omission is the whole point.
// reproKind is what restricts a node's context menu (reproMenuPolicy), replaces its name with a
// role label and overrides its symbol shape (reproRoleLabelOf / reproSatelliteGenderShape) — and,
// on the backend, what drops a node from the CanRisk and XML exports (reproExportFilter). A donor
// who is a real person must keep all of it: 1597 exists precisely so their own family history can
// be recorded and explored.
//
// `member` here is one of the shallow copies made at the top of deriveReproNodes, and both fields
// are ASSIGNED a fresh array rather than mutated, so the real member record is untouched and none
// of this can reach a save path.
export function promoteDonorToMember(
  donor,
  targets,
  resolve,
  forbiddenPids = []
) {
  if (!config.ENABLE_DONOR_IDENTITY) return null;
  const ref = donor && donor.memberId;
  if (ref == null || ref === '') return null;
  const member = resolve(ref);
  if (!member) return null; // dangling ref (deleted member, stale import) → satellite
  // A self-loop cannot be drawn or read: the carrier of the pregnancy and the offspring
  // themselves are never their own donor. The editor blocks both, but a record can arrive from an
  // import or a client that predates that validation.
  if (forbiddenPids.some((pid) => String(pid) === String(member.pid))) {
    return null;
  }
  const own = new Set((member.donorOfPids || []).map(Number));
  targets.forEach((pid) => {
    if (String(pid) !== String(member.pid)) own.add(Number(pid));
  });
  member.donorOfPids = [...own];
  // Role annotation, so "Sperm donor" can print beside a real person's name behind the opt-in
  // `donorRole` label. MERGED, never replaced — one person can donate different gametes to
  // different pregnancies, and a replace would drop the earlier role.
  const roles = new Set(member.donorRoles || []);
  if (donor.role) roles.add(donor.role);
  member.donorRoles = [...roles];
  return member;
}

export function deriveReproNodes(members) {
  if (!config.ENABLE_REPRO_NODES || !Array.isArray(members)) {
    return members || [];
  }
  // TGCLOUD-1613 — a donor can also be recorded against someone already BORN, which has no
  // pregnancy record at all, so the early-out has to consider `gameteDonors` too. Without this the
  // whole pass would skip a family whose only reproductive data is a born-person donor.
  const anyRecords = members.some(
    (m) =>
      (Array.isArray(m.pregnancies) && m.pregnancies.length > 0) ||
      (config.ENABLE_DONOR_IDENTITY &&
        Array.isArray(m.gameteDonors) &&
        m.gameteDonors.length > 0)
  );
  if (!anyRecords) return members; // byte-identical when there is no reproductive data

  // Shallow-copy every member so childrenIds can be extended without mutating the input.
  const result = members.map((m) => ({
    ...m,
    childrenIds: [...(m.childrenIds || [])]
  }));
  const findByMemberId = (id) =>
    id == null
      ? null
      : result.find(
          (m) =>
            String(m._id) === String(id) ||
            String(m.uid) === String(id) ||
            String(m.pid) === String(id)
        );

  let nextPid =
    Math.max(0, ...result.map((m) => (typeof m.pid === 'number' ? m.pid : 0))) +
    1;
  const derived = [];

  for (const carrier of result) {
    const pregs = Array.isArray(carrier.pregnancies) ? carrier.pregnancies : [];
    for (const rawRec of pregs) {
      // Normalise FHQ-vs-Bennett outcome + field-name differences on read (legacy FHQ
      // records store `delivered`/`completedWeeks`/`twins_triplets`); pedigree-authored
      // records pass through unchanged.
      const rec = normalizePregnancyRecord(rawRec);
      // A `hidden` record is kept as history (still listed in the Reproductive sidebar) but
      // nothing about it is drawn — no fetus nodes and no donors. Whether an individual fetus
      // draws is decided per fetus below (live birth / bornMemberId / hideInPedigree), because
      // one twin can be born or hidden while its sibling is not.
      if (rec.hidden) continue;

      // `gender` is the assigned sex at birth: a non-male carrier occupies the mother slot
      // (they gestate). The other genetic parent slot is the in-graph partner only when the
      // pregnancy was partner-conceived; a donor is never a parent (it is a satellite).
      const carrierIsMother = carrier.gender !== GENDER.male;
      const partner = partnerConceived(rec)
        ? findByMemberId(rec.otherParentMemberId)
        : null;

      const uid = String(rec._id ?? `preg-${carrier.pid}`);
      // ONE NODE PER FETUS (Desktop parity). `normalizePregnancyRecord` guarantees `fetuses` is
      // populated — a legacy record yields a single synthesised fetus, so a one-fetus pregnancy
      // renders exactly as it did before this change.
      const fetuses = Array.isArray(rec.fetuses) ? rec.fetuses : [];
      const fetusPids = [];
      const bornPids = [];

      fetuses.forEach((f, i) => {
        const fetus = f || {};
        // A born fetus is a real person now — it draws no fetus node, but a donor-conceived birth
        // keeps its donors pointing at that child (below).
        if (fetus.bornMemberId != null) {
          const born = findByMemberId(fetus.bornMemberId);
          if (born) bornPids.push(born.pid);
          return;
        }
        if (!fetusRendersAsNode(fetus)) return;

        const pid = nextPid++;
        fetusPids.push(pid);
        derived.push({
          pid,
          // The FIRST fetus keeps the record's own id as its uid, so pins / custom positions /
          // saved views captured before fetuses existed still resolve. Later fetuses get a
          // deterministic suffix (`:f1`, `:f2`…), which cannot collide with a donor uid
          // (`:0`, `:1`… — digits, never an `f` prefix).
          uid: i === 0 ? uid : `${uid}:f${i}`,
          gender:
            fetus.fetalSexKnown && fetus.gender ? fetus.gender : GENDER.unknown,
          isFetus: true,
          reproKind: REPRO_KIND.pregnancy,
          // Symbol text prints INSIDE the symbol (REQ-G7) — Catherine's use is numbering the
          // fetuses of a twin pregnancy.
          pedigreeSymbolText: fetus.pedigreeSymbolText,
          // Beside the symbol, behind the opt-in `fetusName` Reproductive label. Named
          // distinctly (not `name`) so it can never be mistaken for a person's name field.
          fetusName: fetus.name,
          // Fetus diagnoses reach the render as a member-shaped `diagnoses` array, so the
          // existing disease-shading pass shades an affected fetus with no special-casing
          // (clipped to the triangle/diamond by the symbol's own clip twin).
          ...(Array.isArray(fetus.diagnoses) && fetus.diagnoses.length
            ? { diagnoses: fetus.diagnoses }
            : {}),
          // Dysmorphic features (REQ-E1) reach the render the same way, as a member-shaped
          // `phenotypes` array — collectPhenotypeSource then finds them and the existing phenotype
          // label pass prints them beside the symbol.
          ...(Array.isArray(fetus.phenotypes) && fetus.phenotypes.length
            ? { phenotypes: fetus.phenotypes }
            : {}),
          pregnancy: {
            // Per-FETUS outcome drives the symbol shape; gestational age and multiplicity are
            // properties of the pregnancy and so are shared by its fetuses.
            outcome: fetus.outcome,
            gestationalWeeks: rec.gestationalWeeks,
            fetalSexKnown: !!fetus.fetalSexKnown,
            sexDeterminationMethod: fetus.sexDeterminationMethod,
            karyotype: fetus.karyotype,
            multiplicity: rec.multiplicity
          },
          motherId: carrierIsMother
            ? carrier.pid
            : partner
            ? partner.pid
            : null,
          fatherId: carrierIsMother
            ? partner
              ? partner.pid
              : null
            : carrier.pid,
          spouseIds: [],
          childrenIds: [],
          siblingIds: []
        });
        carrier.childrenIds.push(pid);
        if (partner) partner.childrenIds.push(pid);
      });

      // MULTIPLE GESTATION → draw them as twins (REQ-G3): slanted lines from a shared apex on
      // the sibship drop. Fetuses of one pregnancy already share both parent slots, so
      // createSiblingGroups puts them in one sibling group; these three fields are what promote
      // that group to a twin SUB-group (the same contract addSiblingsToRecords writes for people).
      //
      // ZYGOSITY follows Bennett (Tim's ruling: Bennett prevails over Desktop's single twin style).
      // The recorded zygosity drives the notation — a horizontal bar between monozygotic twins,
      // plain slanted lines for dizygotic, and a "?" when it is genuinely unknown. Unrecorded
      // zygosity therefore reads as "?" honestly, rather than asserting a zygosity we do not know.
      // TGCLOUD-1612 — the group spans the FETUSES **and** any co-fetus already converted to a
      // person. It used to be formed from `fetusPids` alone, so converting one twin of a pair
      // dropped the count to 1 and the twin notation vanished; meanwhile the person side only links
      // a child to co-fetuses that ALREADY carry a `bornMemberId`, so neither mechanism fired and
      // the relationship existed nowhere. A live-birth + stillbirth pair (Kathryn's repro) is stuck
      // there permanently, because a stillbirth cannot convert at all.
      //
      // `bornPids` is already collected above and already used for the donor lines below; the twin
      // group now uses that same union. Being DERIVED, this also repairs families already in the
      // broken state with no migration.
      const twinPids = [...fetusPids, ...bornPids];
      if (twinPids.length > 1) {
        const siblingType =
          SIBLING_TYPE_BY_COUNT[twinPids.length] || SIBLING_TYPE.quadruplet;
        const twinType =
          ZYGOSITY_TO_TWIN_TYPE[rec.zygosity] ?? TWIN_TYPE.unknown;
        const bornMembers = result.filter((m) => bornPids.includes(m.pid));
        // A group id already PERSISTED on a born child (linkTwinMembers writes one once a second
        // twin converts) wins, so this pass is a safety net rather than a rival owner of the group.
        const groupId =
          bornMembers.map((m) => m.twinSubGroupId).find(Boolean) ||
          `preg-twins-${uid}`;
        const relationsFor = (pid) =>
          twinPids
            .filter((p) => p !== pid)
            .map((withPid) => ({ withPid, type: twinType }));

        for (const node of derived) {
          if (!fetusPids.includes(node.pid)) continue;
          node.twinSubGroupId = groupId;
          node.siblingType = siblingType;
          node.twinRelations = relationsFor(node.pid);
          // Fetus-only, deliberately NOT widened to the born co-twin: the twin sub-group is formed
          // by grouping siblings on `twinSubGroupId` (Family.js), never from `siblingIds`, so
          // nothing needs it and widening it would touch a consumer we have not checked.
          node.siblingIds = fetusPids.filter((p) => p !== node.pid);
        }

        // A BORN co-twin is a REAL member, so only the twin fields may be written. These are
        // shallow copies made at the top of this function, so this stays render-only.
        for (const member of bornMembers) {
          member.twinSubGroupId = groupId;
          member.siblingType = siblingType;
          member.isTwin = siblingType === SIBLING_TYPE.twin;
          // MERGED, never replaced: a person can be twinned with someone OUTSIDE this pregnancy,
          // and overwriting the array would erase a hand-entered relation.
          const kept = (member.twinRelations || []).filter(
            (r) => !twinPids.some((p) => String(p) === String(r?.withPid))
          );
          member.twinRelations = [...kept, ...relationsFor(member.pid)];
          // `siblingIds` is deliberately UNTOUCHED. A real member has real sibling edges to
          // non-twin siblings; replacing them with "just the twins" would drop those siblings out
          // of the sibship and visibly change the chart.
        }
      }

      // Gamete donors — satellites carrying donorOfPids (never childrenIds / a mating line).
      // A donor contributed to the PREGNANCY, so it points at every fetus of it (and at any
      // already-born child): with more than one target in the same sibship the layout draws ONE
      // line to the sibship bar rather than a diagonal per fetus (Bennett 5.1c).
      const donorTargets = [...fetusPids, ...bornPids];
      const donors = Array.isArray(rec.donors) ? rec.donors : [];
      if (donorTargets.length) {
        donors.forEach((d, i) => {
          // TGCLOUD-1597 — a donor we can NAME is promoted to its real member and draws from
          // there; only an anonymous (or unresolvable) donor still mints a satellite.
          if (
            promoteDonorToMember(d, donorTargets, findByMemberId, [
              carrier.pid,
              ...donorTargets
            ])
          ) {
            return;
          }
          derived.push({
            pid: nextPid++,
            uid: `${uid}:${i}`,
            gender: DONOR_GENDER[d.role] ?? GENDER.unknown,
            reproKind: REPRO_KIND.gameteDonor,
            donorType: d.role,
            donorOfPids: donorTargets,
            spouseIds: [],
            childrenIds: [],
            siblingIds: []
          });
        });
      }
    }
  }

  // === Donor conception for someone ALREADY BORN (TGCLOUD-1613) =============================
  // Donors used to be reachable only through the pregnancy workflow, so a family first seen after
  // the children were born had to invent a pregnancy purely to get the donors drawn. That is what
  // this pass removes. The donor is recorded on the CHILD as `gameteDonors: [{ role, memberId }]`,
  // mirroring how adopted-in children carry bioFatherId/bioMotherId.
  //
  // NO pregnancy record is synthesised for these people, deliberately: gravidity and parity are
  // derived from the mother's pregnancy records, so a placeholder pregnancy would silently inflate
  // her obstetric history — corrupt clinical data in exchange for a rendering convenience.
  //
  // The donor resolves to the SAME node and edge shape as a pregnancy-stage donor (promoted member
  // when named, satellite when anonymous), so this adds a second place the data can live but no
  // second render path.
  if (config.ENABLE_DONOR_IDENTITY) {
    for (const child of result) {
      const links = Array.isArray(child.gameteDonors) ? child.gameteDonors : [];
      links.forEach((link, i) => {
        if (!link) return;
        const targets = [child.pid];
        if (promoteDonorToMember(link, targets, findByMemberId, [child.pid])) {
          return;
        }
        derived.push({
          pid: nextPid++,
          // `donor-` prefixed so it can never collide with a pregnancy donor's uid
          // (`<recordId>:<n>`) or a later fetus's (`<recordId>:f<n>`).
          uid: `donor-${child.uid ?? child.pid}:${i}`,
          gender: DONOR_GENDER[link.role] ?? GENDER.unknown,
          reproKind: REPRO_KIND.gameteDonor,
          donorType: link.role,
          donorOfPids: targets,
          spouseIds: [],
          childrenIds: [],
          siblingIds: []
        });
      });
    }
  }

  const all = [...result, ...derived];

  // === DONOR PROXIES (TGCLOUD-1597) =========================================================
  // "Have ability to create a proxy for donor (currently proxy only available for spouse … so need
  // enhancement to the proxy capabilities)."
  //
  // Why this runs HERE rather than in the ordinary proxy pass: proxies are derived in ChartBox,
  // BEFORE this function, so at that point a promoted donor has no `donorOfPids` yet — it is
  // stamped above. The intents are read off the same `proxyIds` array the spouse pass uses, so
  // there is no second place to store a proxy, and a spouse pairing has already been consumed
  // upstream (spouse wins, so an existing saved proxy cannot change meaning).
  //
  // LIMITATION, stated rather than hidden: the anchor must be a REAL member. A derived fetus node's
  // pid is allocated per render (`nextPid++`) and is not stable across loads, so a proxy anchored to
  // an unborn fetus could not survive a reload. It is also unnecessary — the satellite pass already
  // seats a donor next to the carrier, so there is no long line to shorten until the child is born.
  if (config.ENABLE_DONOR_IDENTITY) {
    const realPids = new Set(result.map((m) => m.pid));
    const donorIntents = [];
    for (const m of all) {
      const proxyIds = Array.isArray(m.proxyIds) ? m.proxyIds : [];
      for (const proxyId of proxyIds) {
        if (
          (m.donorOfPids || []).includes(proxyId) &&
          !(m.spouseIds || []).includes(proxyId) &&
          realPids.has(proxyId)
        ) {
          donorIntents.push({ originalPid: m.pid, proxyId });
        }
      }
    }
    if (donorIntents.length) return deriveProxyNodes(all, donorIntents);
  }

  return all;
}
